import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineSetting } from "react-icons/ai";
import { RiHistoryLine } from "react-icons/ri";
import { MdClear } from "react-icons/md";
import moment from "moment";
import styles from "./style/EnterpriseList.module.scss";
import historyStyles from "./style/Dashboard.module.scss";
import Button from "../Utilities/Button";
import AlertSwitch from "../Utilities/AlertSwitch";
import Alert from "../Utilities/Alert";
import EmptyBox from "../Utilities/EmptyBox";
import Modal from "../Utilities/Modal";
import Table from "../Utilities/Table";
import Paginate from "../Utilities/Paginate";
import LoadingTable from "../Utilities/LoadingTable";
import TextField from "../Utilities/TextField";
import TrialPlanPanel from "./TrialPlanPanel";
import ModalHistoryStartOne from "./ModalHistoryStartOne";
import { getServiceName } from "../../constant/SERVICE_NAME_MAP";
import { selectMenu } from "../../store/slice/manager/menu";
import { selectPermissionSettings } from "../../store/slice/permissionSettings";
import {
  getPersonalPlanDetail,
  activatePersonalService,
  activateUserStartOne,
  postGiftStartOne,
  getPersonalSubscriptionHistory,
  getHistoryStartOne,
} from "../../store/slice/manager/enterpriseCompany";

const START_ONE_ID = 8;
// 「AI 分析」服務的後端英文名（software_services.name）。開通此服務時需連帶開通 START-ONE。
const AI_ANALYSIS_NAME = "AI Analysis";

// 服務狀態（比照 EnterpriseList.getStatusMeta）
const getStatusMeta = (data) => {
  const activated =
    data?.activation_state === true ||
    data?.activation_state === 1 ||
    data?.activation_state === undefined ||
    data?.activation_state === null;
  if (!activated) {
    // 停用狀態：曾開通過（start_at 為真實日期）→ 已停用；從來沒開通過 → 未啟用。
    const t = data?.start_at;
    const wasEverActivated = !!t && !Number.isNaN(new Date(t).getTime());
    return wasEverActivated
      ? { label: "已停用", cls: "off" }
      : { label: "未啟用", cls: "off" };
  }
  const st = data?.subscription_status;
  if (st === "on_trial") return { label: "試用中", cls: "trial" };
  if (st === "subscribing")
    return {
      label: data?.software_services_id === START_ONE_ID ? "已啟用" : "訂閱中",
      cls: "on",
    };
  if (st === "no_purchase") return { label: "未購買", cls: "off" };
  if (st === "no_renew") return { label: "未續訂", cls: "off" };
  return { label: "已停用", cls: "off" };
};

// 個人贈送 START-ONE 儲值點（打 user 端點）
const GiftPointsPopup = React.memo(({ userId, onClose, restart }) => {
  const dispatch = useDispatch();
  const [points, setPoints] = useState("");
  const [remark, setRemark] = useState("");
  const [error, setError] = useState(false);

  const handleConfirm = useCallback(() => {
    if (!points) {
      setError(true);
      return;
    }
    dispatch(
      postGiftStartOne({ user_id: userId, points: Number(points), remark })
    ).then((e) => {
      if (e?.payload?.msg === "success") {
        onClose();
        restart && restart();
      }
    });
  }, [points, remark, userId, dispatch, onClose, restart]);

  return (
    <span className={styles.trialPlanPopup}>
      <span className={styles.trialPlanPopupTop}>
        贈送儲值餘額
        <Button type="tertiary" icon={<MdClear />} onClick={onClose} />
      </span>
      <span className={styles.trialPlanPopupMid}>
        <span className={styles.trialPlanPopupMidRow}>
          <span className={styles.trialPlanPopupMidRowTitle}>儲值餘額</span>
          <span className={styles.trialPlanPopupMidRowValue}>
            <TextField
              placeholder="請輸入儲值餘額"
              value={points}
              onChange={(v) => {
                setPoints(v);
                setError(false);
              }}
              numeric
              error={error}
            />
          </span>
        </span>
        <span className={styles.trialPlanPopupMidRow}>
          <span className={styles.trialPlanPopupMidRowTitle}>備註 (選填)</span>
          <span className={styles.trialPlanPopupMidRowValue}>
            <TextField
              placeholder="請輸入備註"
              value={remark}
              onChange={(v) => setRemark(v)}
            />
          </span>
        </span>
      </span>
      <div className={styles.trialPlanPopupBottom}>
        <Button title="取消" type="secondary" onClick={onClose} />
        <Button title="確認贈送" type="primary" onClick={handleConfirm} />
      </div>
    </span>
  );
});

// 單一服務卡片（個人版）：付費訂閱 read_only 唯讀；grant 可切換；START-ONE 走 user 端點
const PersonalPlanData = ({
  item,
  userId,
  menu,
  canManage,
  openTrialFor,
  setOpenTrialFor,
  openGiftFor,
  setOpenGiftFor,
  onAskToggle,
  onOpenHistory,
  reload,
}) => {
  return item?.map((data, index) => {
    const status = getStatusMeta(data);
    const isStartOne = data?.software_services_id === START_ONE_ID;
    const readOnly = data?.read_only === true; // 付費訂閱不可後台切換
    // grant 尚未設定日期者不可直接切換（需先設定試用）；START-ONE 例外
    const isUnsubscribed = !isStartOne && !data?.expiration_date;
    const menuItem = (menu || []).find(
      (m) => m.id === data?.software_services_id
    );
    const serviceName = getServiceName(
      menuItem?.name ||
        data?.software_services_name ||
        data?.service_name ||
        data?.name
    );
    const showFree =
      !isStartOne && (data?.is_trial || data?.subscription_status === "on_trial");
    const toggleDisabled =
      !canManage || readOnly || (isUnsubscribed && !isStartOne);

    return (
      <React.Fragment key={index}>
        <div className={styles.mpCard}>
          <div className={styles.mpCardTop}>
            <div className={styles.planCardLeft}>
              <AlertSwitch
                disabled={toggleDisabled}
                onChange={() => onAskToggle(data, serviceName)}
                color={{ on: "#5856D6", off: "#5856D64D" }}
                isChecked={data.activation_state}
              />
              <span className={styles.planCardName}>{serviceName}</span>
              {readOnly && (
                <span className={styles.tagUnpurchased}>已付費訂閱</span>
              )}
              {!readOnly && isUnsubscribed && (
                <span className={styles.tagUnpurchased}>未開通</span>
              )}
            </div>
            <div className={styles.rowActions}>
              <span className={`${styles.statusBadge} ${styles[status.cls]}`}>
                {status.label}
              </span>
              {/* 齒輪：START-ONE → 贈送點數；其他 → 開通設定（訂閱中可檢視唯讀） */}
              {isStartOne ? (
                <Button
                  type="pure"
                  icon={<AiOutlineSetting />}
                  onClick={() =>
                    setOpenGiftFor(
                      openGiftFor === data.software_services_id
                        ? ""
                        : data.software_services_id
                    )
                  }
                />
              ) : (
                <Button
                  type="pure"
                  icon={<AiOutlineSetting />}
                  onClick={() =>
                    setOpenTrialFor(
                      openTrialFor?.software_services_id ===
                        data.software_services_id
                        ? ""
                        : data
                    )
                  }
                />
              )}
              {openGiftFor === data.software_services_id && isStartOne && (
                <>
                  <div
                    className={styles.popupBackdrop}
                    onClick={() => setOpenGiftFor("")}
                  />
                  <GiftPointsPopup
                    userId={userId}
                    onClose={() => setOpenGiftFor("")}
                    restart={reload}
                  />
                </>
              )}
            </div>
          </div>

          <div className={styles.dataGrid}>
            <div className={styles.cell}>
              <div className={styles.k}>試用設定</div>
              {showFree ? (
                <div className={styles.v}>
                  <span className={styles.freeTag}>Free</span>
                </div>
              ) : (
                <div className={`${styles.v} ${styles.muted}`}>-</div>
              )}
            </div>
            <div className={styles.cell}>
              <div className={styles.k}>啟用日期</div>
              <div
                className={`${styles.v}${data.start_date ? "" : ` ${styles.muted}`}`}
              >
                {data.start_date || "-"}
              </div>
            </div>
            <div className={styles.cell}>
              <div className={styles.k}>到期日期</div>
              <div className={styles.v}>{data.expiration_date || "-"}</div>
            </div>
            <div className={styles.cell}>
              <div className={styles.k}>停用日期</div>
              <div className={styles.v}>
                {(data.end_at && String(data.end_at).split(" ")[0]) || "-"}
              </div>
            </div>
            <div className={styles.cell}>
              <div className={styles.k}>訂閱購買紀錄</div>
              <div
                className={styles.vLink}
                onClick={() => onOpenHistory(data.software_services_id)}
              >
                <RiHistoryLine /> 查看
              </div>
            </div>
          </div>
        </div>

        {!isStartOne &&
          openTrialFor?.software_services_id ===
            data?.software_services_id && (
            <TrialPlanPanel
              variant="personal"
              userId={userId}
              data={data}
              restart={reload}
              onClose={() => setOpenTrialFor("")}
              // 只有「個人刷卡付費訂閱」(read_only) 鎖住時間；後台手動開通的訂閱/試用仍可調整期間
              readOnly={readOnly}
            />
          )}
      </React.Fragment>
    );
  });
};

const PersonalManagePlanModal = ({ isOpen, onClose, user, onChanged }) => {
  const dispatch = useDispatch();
  const { menu } = useSelector(selectMenu);
  const { auth, permissions } = useSelector(selectPermissionSettings);

  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [openTrialFor, setOpenTrialFor] = useState("");
  const [openGiftFor, setOpenGiftFor] = useState("");
  const [toggleTarget, setToggleTarget] = useState(null); // { data, name }
  // 訂閱購買紀錄（非 START-ONE）：對齊 ModalHistory 版面
  const [history, setHistory] = useState(null); // { serviceId, loading, rows, total, page }
  // START-ONE 購買／贈送紀錄：直接複用 ModalHistoryStartOne
  const [startOneHistoryUser, setStartOneHistoryUser] = useState(null);

  const userId = user?.id;
  const historyLimit = 15;

  const canManage =
    auth?.roles?.find((it) => it.includes("workspace.admin")) ||
    permissions?.includes(
      "tdp.services.enterprise.activate_services_enterprise_bought.center"
    ) ||
    permissions?.includes(
      "tdp.services.enterprise.stop_services_enterprise_bought.center"
    );

  const reload = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    dispatch(getPersonalPlanDetail({ user_id: userId })).then((e) => {
      const list = Array.isArray(e?.payload?.data) ? e.payload.data : [];
      setServices(list.filter((it) => it?.software_services_id != null));
      setLoading(false);
      onChanged && onChanged();
    });
  }, [dispatch, userId, onChanged]);

  useEffect(() => {
    if (isOpen && userId) {
      setServices([]);
      setOpenTrialFor("");
      setOpenGiftFor("");
      setHistory(null);
      setStartOneHistoryUser(null);
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  // 是否為「AI 分析」服務（以後端英文名判斷，menu 未載入時退回 data 上的名稱）。
  const isAiAnalysis = (data) => {
    const menuItem = (menu || []).find(
      (m) => m.id === data?.software_services_id
    );
    const name =
      menuItem?.name ||
      data?.software_services_name ||
      data?.service_name ||
      data?.name;
    return name === AI_ANALYSIS_NAME;
  };

  const handleConfirmToggle = () => {
    const data = toggleTarget?.data;
    if (!data) return;
    const next = !data.activation_state;
    const done = () => {
      setToggleTarget(null);
      reload();
    };
    if (data.software_services_id === START_ONE_ID) {
      dispatch(
        activateUserStartOne({ user_id: userId, activation_state: next })
      ).then(done);
    } else if (next && isAiAnalysis(data)) {
      // 開通「AI 分析」時一併開通 START-ONE（service_id 8）。
      // managed-services 清單來源是 personal_management/activation_state，
      // 故必須打這支才會反映；同時打 start-one-activation 讓 START-ONE 真正啟用。
      Promise.all([
        dispatch(
          activatePersonalService({
            user_id: userId,
            service_id: START_ONE_ID,
            activation_setting: true,
          })
        ),
        dispatch(
          activateUserStartOne({ user_id: userId, activation_state: true })
        ),
        dispatch(
          activatePersonalService({
            user_id: userId,
            service_id: data.software_services_id,
            activation_setting: next,
          })
        ),
      ]).then(done);
    } else {
      dispatch(
        activatePersonalService({
          user_id: userId,
          service_id: data.software_services_id,
          activation_setting: next,
        })
      ).then(done);
    }
  };

  const fetchSubscriptionHistory = useCallback(
    (serviceId, page = 1) => {
      setHistory((prev) => ({
        ...(prev || {}),
        serviceId,
        loading: true,
        page,
        rows: prev?.serviceId === serviceId ? prev.rows : [],
        total: prev?.serviceId === serviceId ? prev.total : 0,
      }));
      dispatch(
        getPersonalSubscriptionHistory({
          user_id: userId,
          service_id: serviceId,
          page,
          limit: historyLimit,
        })
      ).then((e) => {
        setHistory({
          serviceId,
          loading: false,
          page,
          rows: Array.isArray(e?.payload?.rows) ? e.payload.rows : [],
          total: e?.payload?.total || 0,
        });
      });
    },
    [dispatch, userId, historyLimit]
  );

  const openHistory = (serviceId) => {
    // START-ONE：與個人列表「查看紀錄」同一套 ModalHistoryStartOne
    if (serviceId === START_ONE_ID) {
      const historyUserId = user?.user_information?.user_id || userId;
      setStartOneHistoryUser(user);
      dispatch(
        getHistoryStartOne({
          user_id: historyUserId,
          page: 1,
          limit: 10,
        })
      );
      return;
    }
    fetchSubscriptionHistory(serviceId, 1);
  };

  const historyHeaderData = [
    { title: "扣款日期", width: 12 },
    { title: "月租方案", width: 28 },
    { title: "開始日期", width: 12 },
    { title: "結束日期", width: 12 },
    { title: "付款方式", width: 12 },
    { title: "付款金額", width: 12 },
    { title: "付款狀態", width: 12 },
  ];

  if (!isOpen) return null;

  const visibleServices = services.filter(
    (s) => s?.software_services_id !== START_ONE_ID
  );

  const planDataProps = {
    userId,
    menu,
    canManage,
    openTrialFor,
    setOpenTrialFor,
    openGiftFor,
    setOpenGiftFor,
    onAskToggle: (data, name) => setToggleTarget({ data, name }),
    onOpenHistory: openHistory,
    reload,
  };

  return (
    <>
      <div
        className={styles.manageOverlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className={styles.manageModal}>
          <div className={styles.manageHead}>
            <div>
              <div className={styles.manageTitle}>
                {user?.name || user?.email} · 管理方案
              </div>
              <div className={styles.manageSub}>
                {user?.email} ・ 後台開通/試用（不扣款）；已付費訂閱以唯讀併呈
              </div>
            </div>
            <Button type="tertiary" icon={<MdClear />} onClick={onClose} />
          </div>

          <div className={styles.manageBody}>
            {loading && visibleServices.length === 0 ? (
              <div
                style={{
                  padding: "48px 0",
                  textAlign: "center",
                  color: "#86868b",
                }}
              >
                載入中…
              </div>
            ) : visibleServices.length === 0 ? (
              <EmptyBox caption={"目前沒有可管理的方案"} />
            ) : (
              <PersonalPlanData item={visibleServices} {...planDataProps} />
            )}
          </div>

          <div className={styles.manageFoot}>
            <Button title="關閉" type="secondary" onClick={onClose} />
          </div>
        </div>
      </div>

      {/* 開通/停用確認 */}
      <Alert
        isOpen={!!toggleTarget}
        onConfirm={handleConfirmToggle}
        onClose={() => setToggleTarget(null)}
        rightButtonText={toggleTarget?.data?.activation_state ? "停用" : "啟用"}
        leftButtonText="取消"
        mainTitle={
          toggleTarget?.data?.activation_state
            ? `停用「${toggleTarget?.name}」?`
            : `啟用「${toggleTarget?.name}」?`
        }
      />

      {/* START-ONE 購買／贈送紀錄（與個人列表「查看紀錄」同一元件） */}
      <ModalHistoryStartOne
        isOpen={startOneHistoryUser}
        setIsOpen={setStartOneHistoryUser}
        refreshPersonalList={onChanged}
      />

      {/* 訂閱購買紀錄（對齊 ModalHistory 版面） */}
      <Modal isOpen={!!history} size="xxl">
        {history && (
          <div className={historyStyles.ModalContainer}>
            <div
              className={`${historyStyles.modalHeader} ${historyStyles.special}`}
            >
              <div className={historyStyles.modalTitle}>訂閱購買紀錄</div>
              <Button
                type="tertiary"
                icon={<MdClear />}
                onClick={() => setHistory(null)}
              />
            </div>
            <div
              className={`${historyStyles.modalBody} ${historyStyles.nonOverflow}`}
            >
              <Table header={historyHeaderData}>
                {history.loading ? (
                  <LoadingTable row={10} column={historyHeaderData.length} />
                ) : history.rows.length > 0 ? (
                  <tbody>
                    {history.rows.map((order, i) => {
                      const items = order.content || [];
                      const first = items[0] || {};
                      const paid =
                        first.payment_succeeded === true ||
                        order.transaction_log?.status === "paid" ||
                        order.transaction_log?.status === "auth";
                      return (
                        <tr key={order.uuid || i}>
                          <td width={`${historyHeaderData[0].width}%`}>
                            {order.created_at
                              ? moment(order.created_at).format("YYYY-MM-DD")
                              : "-"}
                          </td>
                          <td width={`${historyHeaderData[1].width}%`}>
                            {items.length > 0
                              ? items.map((c, j) => (
                                  <div key={j}>
                                    {getServiceName(c.service_name)}
                                    {c.service_plan_name
                                      ? ` / ${c.service_plan_name}`
                                      : c.plan?.name
                                        ? ` / ${c.plan.name}`
                                        : ""}
                                    {(c.payment_amount || c.payment_amount > 0) && (
                                      <> / NT${c.payment_amount?.toLocaleString()}</>
                                    )}
                                  </div>
                                ))
                              : "-"}
                          </td>
                          <td width={`${historyHeaderData[2].width}%`}>
                            {first.start_date || "-"}
                          </td>
                          <td width={`${historyHeaderData[3].width}%`}>
                            {first.expiration_date || "-"}
                          </td>
                          <td width={`${historyHeaderData[4].width}%`}>
                            {first.payment_method || "-"}
                          </td>
                          <td width={`${historyHeaderData[5].width}%`}>
                            <span className={historyStyles.textPrimary}>
                              {order?.total_amount > 0 ? (
                                `NT$${Number(order.total_amount).toLocaleString()}`
                              ) : (
                                <b>Free</b>
                              )}
                            </span>
                          </td>
                          <td width={`${historyHeaderData[6].width}%`}>
                            {paid ? (
                              <span className={historyStyles.success}>
                                付款成功
                              </span>
                            ) : (
                              <span className={historyStyles.failed}>
                                付款失敗
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  <tbody style={{ height: "450px" }}>
                    <tr>
                      <td colSpan={historyHeaderData.length}>
                        <EmptyBox
                          type={1}
                          caption={"目前還未有任何訂閱購買紀錄"}
                        />
                      </td>
                    </tr>
                  </tbody>
                )}
              </Table>
            </div>
            <div
              className={`${historyStyles.modalFooter} ${historyStyles.normal}`}
            >
              {history.rows.length > 0 && (
                <Paginate
                  onPageChange={(e) =>
                    fetchSubscriptionHistory(
                      history.serviceId,
                      parseInt(e.selected + 1)
                    )
                  }
                  pageCount={history.page || 1}
                  limit={historyLimit}
                  totalPages={Math.ceil((history.total || 0) / historyLimit)}
                  totalData={history.total || 0}
                />
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default PersonalManagePlanModal;
